import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { PhoneInput } from '@/components/ui/phone-input';
import { toast } from 'sonner';
import { ArrowLeft, Phone, ShieldCheck, KeyRound, Loader2, MessageCircle } from 'lucide-react';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: mobile, 2: otp, 3: new password
    const [mobile, setMobile] = useState('');
    const [countryCode, setCountryCode] = useState('+91');
    const [otp, setOtp] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    // Countdown timer for resend OTP
    const startCooldown = () => {
        setCooldown(60);
        const timer = setInterval(() => {
            setCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleRequestOTP = async (e) => {
        e.preventDefault();
        if (!mobile.trim()) {
            toast.error('Please enter your mobile number.');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/auth/forgot-password`, { mobile: `${countryCode}${mobile.trim()}` });
            toast.success('OTP sent! Check your WhatsApp.');
            setStep(2);
            startCooldown();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (cooldown > 0) return;
        setLoading(true);
        try {
            await axios.post(`${API}/auth/forgot-password`, { mobile: `${countryCode}${mobile.trim()}` });
            toast.success('New OTP sent!');
            setOtp('');
            startCooldown();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to resend OTP.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            toast.error('Please enter the complete 6-digit OTP.');
            return;
        }
        setLoading(true);
        try {
            const response = await axios.post(`${API}/auth/verify-otp`, {
                mobile: `${countryCode}${mobile.trim()}`,
                otp,
            });
            setResetToken(response.data.reset_token);
            toast.success('OTP verified!');
            setStep(3);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match.');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/auth/reset-password`, {
                reset_token: resetToken,
                new_password: newPassword,
            });
            toast.success('Password reset successful! Please login with your new password.');
            navigate('/login');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset password.');
        } finally {
            setLoading(false);
        }
    };

    const stepConfig = [
        { icon: Phone, label: 'Mobile' },
        { icon: ShieldCheck, label: 'Verify' },
        { icon: KeyRound, label: 'Reset' },
    ];

    return (
        <div
            className="min-h-screen flex items-center justify-center relative"
            style={{
                backgroundImage: 'url(https://images.pexels.com/photos/4481256/pexels-photo-4481256.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
            data-testid="forgot-password-page"
        >
            <div className="absolute inset-0 bg-black/50" />

            <div className="relative z-10 w-full max-w-md">
                <div className="bg-white p-8 rounded-sm shadow-md border border-stone-200">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <button
                            type="button"
                            onClick={() => (step > 1 ? setStep(step - 1) : navigate('/login'))}
                            className="p-1.5 rounded-sm hover:bg-stone-100 transition-colors"
                            data-testid="back-button"
                        >
                            <ArrowLeft className="h-5 w-5 text-stone-600" />
                        </button>
                        <h2
                            className="text-2xl font-semibold text-[hsl(145,60%,20%)] tracking-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            Reset Password
                        </h2>
                    </div>

                    {/* Step Indicator */}
                    <div className="flex items-center justify-center gap-1 mb-8" data-testid="step-indicator">
                        {stepConfig.map((s, i) => {
                            const StepIcon = s.icon;
                            const isActive = step === i + 1;
                            const isCompleted = step > i + 1;
                            return (
                                <div key={i} className="flex items-center">
                                    <div className="flex flex-col items-center">
                                        <div
                                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isActive
                                                ? 'bg-[hsl(145,60%,20%)] text-white shadow-lg scale-110'
                                                : isCompleted
                                                    ? 'bg-[hsl(145,60%,40%)] text-white'
                                                    : 'bg-stone-100 text-stone-400'
                                                }`}
                                        >
                                            <StepIcon className="h-4 w-4" />
                                        </div>
                                        <span
                                            className={`text-[10px] mt-1 font-medium ${isActive ? 'text-[hsl(145,60%,20%)]' : isCompleted ? 'text-[hsl(145,60%,40%)]' : 'text-stone-400'
                                                }`}
                                        >
                                            {s.label}
                                        </span>
                                    </div>
                                    {i < 2 && (
                                        <div
                                            className={`w-16 h-0.5 mx-2 mb-4 transition-colors duration-300 ${step > i + 1 ? 'bg-[hsl(145,60%,40%)]' : 'bg-stone-200'
                                                }`}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Step 1: Enter Mobile */}
                    {step === 1 && (
                        <form onSubmit={handleRequestOTP} className="space-y-4">
                            <p className="text-sm text-stone-500 mb-4">
                                Enter your registered mobile number. We'll send a verification code to your WhatsApp.
                            </p>
                            <div>
                                <Label htmlFor="mobile" data-testid="label-mobile">Mobile Number</Label>
                                <PhoneInput
                                    value={mobile}
                                    onChange={setMobile}
                                    countryCode={countryCode}
                                    onCountryCodeChange={setCountryCode}
                                    autoFocus
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white"
                                disabled={loading}
                                data-testid="send-otp-button"
                            >
                                {loading ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
                                ) : (
                                    <><MessageCircle className="mr-2 h-4 w-4" /> Send OTP via WhatsApp</>
                                )}
                            </Button>
                        </form>
                    )}

                    {/* Step 2: Enter OTP */}
                    {step === 2 && (
                        <form onSubmit={handleVerifyOTP} className="space-y-4">
                            <p className="text-sm text-stone-500 mb-4">
                                Enter the 6-digit code sent to your WhatsApp on <strong>{countryCode}{mobile}</strong>
                            </p>
                            <div className="flex justify-center py-2">
                                <InputOTP
                                    maxLength={6}
                                    value={otp}
                                    onChange={(val) => setOtp(val)}
                                    data-testid="otp-input"
                                >
                                    <InputOTPGroup>
                                        <InputOTPSlot index={0} className="w-11 h-12 text-lg font-semibold" />
                                        <InputOTPSlot index={1} className="w-11 h-12 text-lg font-semibold" />
                                        <InputOTPSlot index={2} className="w-11 h-12 text-lg font-semibold" />
                                    </InputOTPGroup>
                                    <span className="mx-1 text-stone-300">-</span>
                                    <InputOTPGroup>
                                        <InputOTPSlot index={3} className="w-11 h-12 text-lg font-semibold" />
                                        <InputOTPSlot index={4} className="w-11 h-12 text-lg font-semibold" />
                                        <InputOTPSlot index={5} className="w-11 h-12 text-lg font-semibold" />
                                    </InputOTPGroup>
                                </InputOTP>
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white"
                                disabled={loading || otp.length !== 6}
                                data-testid="verify-otp-button"
                            >
                                {loading ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
                                ) : (
                                    'Verify OTP'
                                )}
                            </Button>
                            <div className="text-center">
                                <button
                                    type="button"
                                    onClick={handleResendOTP}
                                    disabled={cooldown > 0 || loading}
                                    className={`text-sm ${cooldown > 0
                                        ? 'text-stone-400 cursor-not-allowed'
                                        : 'text-[hsl(145,60%,20%)] hover:underline'
                                        }`}
                                    data-testid="resend-otp-button"
                                >
                                    {cooldown > 0 ? `Resend OTP in ${cooldown}s` : 'Resend OTP'}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Step 3: New Password */}
                    {step === 3 && (
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <p className="text-sm text-stone-500 mb-4">
                                Create a new password for your account.
                            </p>
                            <div>
                                <Label htmlFor="newPassword">New Password</Label>
                                <Input
                                    id="newPassword"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    placeholder="At least 6 characters"
                                    className="mt-1"
                                    data-testid="input-new-password"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <Label htmlFor="confirmPassword">Confirm Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    placeholder="Re-enter your password"
                                    className="mt-1"
                                    data-testid="input-confirm-password"
                                />
                            </div>
                            {newPassword && confirmPassword && newPassword !== confirmPassword && (
                                <p className="text-xs text-red-500" data-testid="password-mismatch">
                                    Passwords do not match.
                                </p>
                            )}
                            <Button
                                type="submit"
                                className="w-full bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white"
                                disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                                data-testid="reset-password-button"
                            >
                                {loading ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Resetting...</>
                                ) : (
                                    'Reset Password'
                                )}
                            </Button>
                        </form>
                    )}

                    {/* Back to login link */}
                    <div className="mt-6 text-center">
                        <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="text-sm text-[hsl(145,60%,20%)] hover:underline"
                            data-testid="back-to-login"
                        >
                            Back to Login
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
